import { useEffect, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Bike,
  Bus,
  Car,
  CarFront,
  CarTaxiFront,
  Truck,
  Van,
  createLucideIcon,
  type LucideIcon,
} from 'lucide-react';

/**
 * The three services, the classes in each, and how a machine is drawn.
 *
 * CITYLOAD_HEAVY_EQUIPMENT.md section 2. The server derives `service_kind` from
 * the class (`serviceOf` in `services/api/src/domain/cityload-classes.ts`); this
 * is the same mapping, used only where a row arrives without `serviceKind` or a
 * list has to be narrowed before the API filters it.
 */

export type ServiceKind = 'ride' | 'goods' | 'equipment';

export const SERVICES: readonly { readonly key: ServiceKind; readonly label: string }[] = [
  { key: 'ride', label: 'Rides' },
  { key: 'goods', label: 'Goods' },
  { key: 'equipment', label: 'Heavy equipment' },
];

export const RIDE_CLASSES = ['bike', 'auto', 'cab', 'cab_xl'] as const;
/** Smallest to largest (M088). `two_wheeler` carries a parcel; `bike` carries a passenger. */
export const GOODS_CLASSES = [
  'two_wheeler',
  'three_wheeler',
  'mini_truck',
  'pickup',
  'truck_14ft',
  'truck_19ft',
  'truck',
  'truck_multi_axle',
  'trailer',
] as const;
/** Hourly machines first, then the three that haul per trip (M089). */
export const EQUIPMENT_CLASSES = [
  'jcb',
  'excavator',
  'crane',
  'road_roller',
  'bulldozer',
  'wheel_loader',
  'motor_grader',
  'skid_steer',
  'rock_breaker',
  'forklift',
  'boom_lift',
  'concrete_pump',
  'tractor',
  'dumper',
  'transit_mixer',
  'water_tanker',
] as const;
/** Billed on the work timer: one site, a work type and hours. */
export const HOURLY_MACHINES: ReadonlySet<string> = new Set([
  'jcb',
  'excavator',
  'crane',
  'road_roller',
  'bulldozer',
  'wheel_loader',
  'motor_grader',
  'skid_steer',
  'rock_breaker',
  'forklift',
  'boom_lift',
  'concrete_pump',
  'tractor',
]);

/** Goods priced by distance and weight. The two smallest are per trip and kilometre. */
export const TONNAGE_CLASSES: ReadonlySet<string> = new Set([
  'mini_truck',
  'pickup',
  'truck_14ft',
  'truck_19ft',
  'truck',
  'truck_multi_axle',
  'trailer',
]);

/** Every class the console knows, in the order a list shows them. */
export const ALL_CLASSES: readonly string[] = [
  ...EQUIPMENT_CLASSES,
  ...GOODS_CLASSES,
  ...RIDE_CLASSES,
];

/** How a class is charged for. The server is the authority; this mirrors it. */
export function classBasis(vehicleClass: string): 'hourly' | 'per_trip_or_km' | 'distance_tonnage' {
  if (HOURLY_MACHINES.has(vehicleClass)) return 'hourly';
  return TONNAGE_CLASSES.has(vehicleClass) ? 'distance_tonnage' : 'per_trip_or_km';
}

/** The API's snake_case classes, as the console writes them. */
export const CLASS_LABEL: Readonly<Record<string, string>> = {
  jcb: 'JCB',
  excavator: 'Excavator',
  crane: 'Crane',
  road_roller: 'Road Roller',
  dumper: 'Dumper',
  mini_truck: 'Mini Truck',
  truck: 'Truck',
  bike: 'Bike',
  auto: 'Auto',
  cab: 'Cab',
  cab_xl: 'Cab XL',
  two_wheeler: 'Two Wheeler',
  three_wheeler: 'Three Wheeler',
  pickup: 'Pickup',
  truck_14ft: 'Truck 14 ft',
  truck_19ft: 'Truck 19 ft',
  truck_multi_axle: 'Multi-axle Truck',
  trailer: 'Trailer',
  bulldozer: 'Bulldozer',
  wheel_loader: 'Wheel Loader',
  motor_grader: 'Motor Grader',
  skid_steer: 'Skid Steer',
  rock_breaker: 'Rock Breaker',
  forklift: 'Forklift',
  boom_lift: 'Boom Lift',
  concrete_pump: 'Concrete Pump',
  tractor: 'Tractor',
  transit_mixer: 'Transit Mixer',
  water_tanker: 'Water Tanker',
};

export function classLabel(vehicleClass: string | null | undefined): string {
  if (vehicleClass === null || vehicleClass === undefined) return 'Not recorded';
  /* A class added to the catalogue after this build has no name here. Its code
     read as words ("piling_rig" as "Piling rig") beats printing the code. */
  return (
    CLASS_LABEL[vehicleClass] ??
    vehicleClass.replace(/_/g, ' ').replace(/^./, (first) => first.toUpperCase())
  );
}

export function serviceOf(vehicleClass: string | null | undefined): ServiceKind | null {
  if (vehicleClass === null || vehicleClass === undefined) return null;
  if ((EQUIPMENT_CLASSES as readonly string[]).includes(vehicleClass)) return 'equipment';
  if ((RIDE_CLASSES as readonly string[]).includes(vehicleClass)) return 'ride';
  if ((GOODS_CLASSES as readonly string[]).includes(vehicleClass)) return 'goods';
  return null;
}

export function isHourlyMachine(vehicleClass: string | null | undefined): boolean {
  return vehicleClass !== null && vehicleClass !== undefined && HOURLY_MACHINES.has(vehicleClass);
}

export function isService(value: string | null): value is ServiceKind {
  return value === 'ride' || value === 'goods' || value === 'equipment';
}

/* ------------------------------------------------------------------ icons */

/*
  Lucide draws no construction machine, so these five are drawn here on
  lucide's own terms: a 24 unit grid, a 2px round stroke, no fill. Made with
  `createLucideIcon`, they take the same props as every other icon in the
  console and sit in a stat tile or a nav row without a special case.
*/

/** Backhoe loader: loader bucket in front, digging arm behind. */
export const JcbIcon = createLucideIcon('cl-jcb', [
  ['circle', { cx: '7.5', cy: '17.5', r: '2.5', key: 'w1' }],
  ['circle', { cx: '17', cy: '17.5', r: '2.5', key: 'w2' }],
  ['path', { d: 'M10 17.5h4.5', key: 'axle' }],
  ['path', { d: 'M5 17.5H4V13h15.5', key: 'body' }],
  ['path', { d: 'M8 13V8h5l2 5', key: 'cab' }],
  ['path', { d: 'M19.5 13 22 11v5h-2.5', key: 'bucket' }],
  ['path', { d: 'M4 13 6 5 2 8.5V11', key: 'arm' }],
]);

/** Tracked excavator with its boom and bucket. */
export const ExcavatorIcon = createLucideIcon('cl-excavator', [
  ['rect', { x: '2', y: '16', width: '13', height: '5', rx: '2.5', key: 'track' }],
  ['path', { d: 'M4 16v-3h9v3', key: 'body' }],
  ['path', { d: 'M6 13V9h4v4', key: 'cab' }],
  ['path', { d: 'M11 11l5-7 5 6', key: 'boom' }],
  ['path', { d: 'M21 10v3.5l-2.5 1', key: 'bucket' }],
]);

/** Mobile crane (hydra): carrier, slewing base, boom and hook. */
export const CraneIcon = createLucideIcon('cl-crane', [
  ['circle', { cx: '6', cy: '18', r: '2', key: 'w1' }],
  ['circle', { cx: '16', cy: '18', r: '2', key: 'w2' }],
  ['path', { d: 'M8 18h6', key: 'axle' }],
  ['path', { d: 'M4 18H2v-4h20v4h-4', key: 'body' }],
  ['path', { d: 'M4 14v-3h5v3', key: 'base' }],
  ['path', { d: 'M6.5 11 20 3', key: 'boom' }],
  ['path', { d: 'M20 3v5', key: 'rope' }],
  ['path', { d: 'M20 8a1.5 1.5 0 1 1-1.5 1.5', key: 'hook' }],
]);

/** Road roller: the drum, the rear wheel, the canopy. */
export const RoadRollerIcon = createLucideIcon('cl-road-roller', [
  ['circle', { cx: '7', cy: '16', r: '5', key: 'drum' }],
  ['circle', { cx: '19', cy: '18.5', r: '2.5', key: 'wheel' }],
  ['path', { d: 'M12 16h4.5', key: 'frame' }],
  ['path', { d: 'M7 11V9h14v7', key: 'body' }],
  ['path', { d: 'M13 9V4', key: 'post1' }],
  ['path', { d: 'M20 9V4', key: 'post2' }],
  ['path', { d: 'M12 4h9', key: 'roof' }],
]);

/** Tipper: the cab, and the body tipped up behind it. */
export const DumperIcon = createLucideIcon('cl-dumper', [
  ['circle', { cx: '6.5', cy: '18', r: '2', key: 'w1' }],
  ['circle', { cx: '17.5', cy: '18', r: '2', key: 'w2' }],
  ['path', { d: 'M8.5 18h7', key: 'axle' }],
  ['path', { d: 'M4.5 18H2v-3h13', key: 'chassis' }],
  ['path', { d: 'M15 18V10h4l3 4v4h-2.5', key: 'cab' }],
  ['path', { d: 'M2 12 4 6l10 3-1 3', key: 'tipper' }],
]);

export const CLASS_ICON: Readonly<Record<string, LucideIcon>> = {
  jcb: JcbIcon,
  excavator: ExcavatorIcon,
  crane: CraneIcon,
  road_roller: RoadRollerIcon,
  dumper: DumperIcon,
  mini_truck: Van,
  truck: Truck,
  bike: Bike,
  auto: CarTaxiFront,
  cab: Car,
  cab_xl: CarFront,
  /* M088 and M089. No new drawings: each takes the icon of the machine it is
     closest to, and the label beside it says which one it is. */
  two_wheeler: Bike,
  three_wheeler: Van,
  pickup: Van,
  truck_14ft: Truck,
  truck_19ft: Truck,
  truck_multi_axle: Truck,
  trailer: Truck,
  bulldozer: JcbIcon,
  wheel_loader: JcbIcon,
  motor_grader: RoadRollerIcon,
  skid_steer: JcbIcon,
  rock_breaker: ExcavatorIcon,
  forklift: JcbIcon,
  boom_lift: CraneIcon,
  concrete_pump: CraneIcon,
  tractor: JcbIcon,
  transit_mixer: DumperIcon,
  water_tanker: DumperIcon,
  /* M091, hired to drive yourself. Each has a picture now; this is what a row
     falls back to if its file is ever missing. */
  rent_two_wheeler: Bike,
  scooter: Bike,
  motorcycle: Bike,
  sports_bike: Bike,
  cruiser_bike: Bike,
  electric_scooter: Bike,
  rent_car: Car,
  hatchback: Car,
  sedan: Car,
  compact_suv: CarFront,
  suv: CarFront,
  muv: CarFront,
  luxury_car: Car,
  electric_car: Car,
  rent_bus: Bus,
  van: Van,
  tempo_traveller: Van,
  mini_bus: Bus,
  bus: Bus,
};

export function classIcon(vehicleClass: string | null | undefined): LucideIcon {
  return (
    (vehicleClass === null || vehicleClass === undefined ? undefined : CLASS_ICON[vehicleClass]) ??
    Truck
  );
}

/* --------------------------------------------------------------- pictures */

/*
  THE SAME PICTURES THE WEBSITE AND THE PHONE APPS SHOW.

  The console drew every machine as a line icon, and M088 and M089 added
  eighteen classes with no drawing of their own, so a forklift, a bulldozer and
  a tractor were all the backhoe. An owner looking down a list of their own
  machines could not tell them apart without reading.

  `scripts/vehicle-image.py` writes one small WebP per class into `./vehicles`.
  Vite gives each a hashed address; nothing is fetched until a picture is on
  screen. A class with no file here (one added to the catalogue after this
  build) falls back to its line icon.
*/
const PICTURE_FILES = import.meta.glob<string>('./vehicles/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

export const CLASS_PICTURE: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(PICTURE_FILES).map(([path, url]) => [
    path.slice(path.lastIndexOf('/') + 1, -'.webp'.length),
    url,
  ]),
);

/* A hire family is a heading over its sub-types, so it borrows the picture of
   the one most people mean by it. */
const FAMILY_PICTURE: Readonly<Record<string, string>> = {
  rent_two_wheeler: 'scooter',
  rent_car: 'sedan',
  rent_bus: 'bus',
};

export function classPicture(vehicleClass: string | null | undefined): string | null {
  if (vehicleClass === null || vehicleClass === undefined) return null;
  return CLASS_PICTURE[FAMILY_PICTURE[vehicleClass] ?? vehicleClass] ?? null;
}

export type PictureSize = 'xs' | 'sm' | 'md' | 'lg';

/* The line icon that stands in when there is no picture, sized to the tile. */
const FALLBACK_ICON: Readonly<Record<PictureSize, number>> = { xs: 14, sm: 18, md: 26, lg: 40 };

/**
 * A class as a picture, on the warm tile the website uses.
 *
 * Decorative: every place that draws one writes the name beside it, so the
 * image carries no alt text and a screen reader hears the name once.
 */
export function ClassPicture({
  vehicleClass,
  size = 'sm',
}: {
  readonly vehicleClass: string | null | undefined;
  readonly size?: PictureSize;
}): ReactElement {
  const picture = classPicture(vehicleClass);
  const Icon = classIcon(vehicleClass);
  return (
    <span
      className={`cl-pic cl-pic--${size}${picture === null ? ' cl-pic--icon' : ''}`}
      aria-hidden="true"
    >
      {picture === null ? (
        <Icon size={FALLBACK_ICON[size]} strokeWidth={2} />
      ) : (
        <img src={picture} alt="" loading="lazy" decoding="async" draggable={false} />
      )}
    </span>
  );
}

/** A class as a row reads it: its picture, then its name. */
export function ClassName({
  vehicleClass,
  size = 'sm',
}: {
  readonly vehicleClass: string | null | undefined;
  readonly size?: PictureSize;
}): ReactElement {
  return (
    <span className="cl-class">
      <ClassPicture vehicleClass={vehicleClass} size={size} />
      <span>{classLabel(vehicleClass)}</span>
    </span>
  );
}

/* ------------------------------------------------------------ class picker */

const GROUP_TITLE: Readonly<Record<string, string>> = {
  equipment: 'Construction machines',
  goods: 'Goods vehicles',
  /* M091: listed for a customer to collect and drive. Never sent with a driver. */
  hire: 'Self-drive hire',
  ride: 'Taxi and auto',
  other: 'Other',
};
const GROUP_ORDER = ['equipment', 'goods', 'hire', 'ride', 'other'] as const;

/**
 * Choosing what kind of machine it is, by looking at it.
 *
 * This was a dropdown of twenty-five names. An owner registering a wheel
 * loader had to know that TeraAgo calls it a wheel loader and not a loader, a
 * payloader or a JCB, and the dropdown opened on JCB, so the wrong answer was
 * the one already filled in. A picture is recognised before a name is read.
 *
 * NOTHING IS CHOSEN TO BEGIN WITH (the caller passes `''`), so the grid is
 * open and the form cannot be sent with a type nobody picked. Once one is
 * picked the grid folds to that one machine, large, with a way back: twenty
 * five tiles should not stand between the choice and the rest of the form.
 *
 * A radio group to assistive technology, which is what it is.
 */
export function ClassPicker({
  id,
  label,
  value,
  onChange,
  options,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /* `group` and `basis` come from the server's catalogue when the caller has
     it; a class this build has never heard of is otherwise filed under Other. */
  readonly options: readonly {
    readonly value: string;
    readonly label: string;
    readonly group?: string;
    readonly basis?: string;
  }[];
}): ReactElement {
  const [open, setOpen] = useState(value === '');
  const chosen = options.find((o) => o.value === value);
  const groups = GROUP_ORDER.map((key) => ({
    key,
    options: options.filter((o) => groupOf(o) === key),
  })).filter((group) => group.options.length > 0);

  if (!open && chosen !== undefined) {
    return (
      <div className="cl-field cl-form__wide">
        <span className="cl-field__label" id={`${id}-label`}>
          {label}
        </span>
        <div className="cl-picked" aria-labelledby={`${id}-label`} role="group">
          <ClassPicture vehicleClass={chosen.value} size="lg" />
          <span className="cl-picked__text">
            <strong>{chosen.label}</strong>
            <span className="cl-quiet">
              {groupOf(chosen) === 'hire'
                ? 'Hired out by the day, the hour or the kilometre'
                : (BASIS_TEXT[chosen.basis ?? ''] ?? BASIS_TEXT[classBasis(chosen.value)])}
            </span>
          </span>
          <button
            type="button"
            className="cl-btn cl-btn--ghost cl-btn--sm"
            onClick={() => setOpen(true)}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cl-field cl-form__wide">
      <span className="cl-field__label" id={`${id}-label`}>
        {label}
      </span>
      <div className="cl-picker" role="radiogroup" aria-labelledby={`${id}-label`}>
        {groups.map((group) => (
          <div key={group.key} className="cl-picker__group">
            {groups.length > 1 ? (
              <p className="cl-picker__title">{GROUP_TITLE[group.key]}</p>
            ) : null}
            <div className="cl-picker__grid">
              {group.options.map((option) => {
                const on = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`cl-picker__tile${on ? ' cl-picker__tile--on' : ''}`}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <ClassPicture vehicleClass={option.value} size="md" />
                    <span className="cl-picker__name">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function groupOf(option: { readonly value: string; readonly group?: string }): string {
  const group = option.group ?? serviceOf(option.value) ?? 'other';
  return group in GROUP_TITLE ? group : 'other';
}

/** How a class is charged, as the owner would say it. */
const BASIS_TEXT: Readonly<Record<string, string>> = {
  hourly: 'Charged by the hour, at one site',
  per_trip_or_km: 'Charged per trip or per kilometre',
  distance_tonnage: 'Charged by distance and weight',
};

/* ----------------------------------------------------------- service tabs */

/**
 * The `?service=` choice on a list page. `null` is All.
 *
 * In the address rather than in state, so a dispatcher can send a colleague
 * "the heavy equipment bookings" as a link, and Back returns to the tab.
 */
export function useServiceParam(): readonly [
  ServiceKind | null,
  (next: ServiceKind | null) => void,
] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('service');
  const service = isService(raw) ? raw : null;
  const set = (next: ServiceKind | null): void => {
    const copy = new URLSearchParams(params);
    if (next === null) copy.delete('service');
    else copy.set('service', next);
    setParams(copy, { replace: true });
  };
  return [service, set] as const;
}

export function ServiceTabs({
  value,
  onChange,
}: {
  readonly value: ServiceKind | null;
  readonly onChange: (next: ServiceKind | null) => void;
}): ReactElement {
  const tabs: readonly { key: ServiceKind | null; label: string }[] = [
    { key: null, label: 'All' },
    ...SERVICES,
  ];
  return (
    <div className="cl-seg cl-svctabs" role="group" aria-label="Service">
      {tabs.map((tab) => (
        <button
          key={tab.label}
          type="button"
          aria-pressed={value === tab.key}
          className={value === tab.key ? 'cl-seg__on' : ''}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- work timer */

/** "2h 14m", "45m", "0m". Whole minutes, never a stopwatch's seconds. */
export function formatWorked(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

/** "4 hr", "2.5 hr". The API sends numeric columns as strings sometimes. */
export function hoursText(value: number | string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${Number.isInteger(n) ? n : n.toFixed(1)} hr`;
}

/** The current time, moving once every `everyMs`. */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

/**
 * "Working 2h 14m", counted from `workStartedAt`.
 *
 * The start is the server's stamp from the customer's start code, so this only
 * counts forward from it; it never decides the bill. The bill is `billedHours`.
 */
export function WorkTimer({
  startedAt,
  prefix = 'Working',
}: {
  readonly startedAt: string;
  readonly prefix?: string;
}): ReactElement {
  const now = useNow();
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return <span className="cl-none">Start not recorded</span>;
  return (
    <span className="cl-worktimer" role="timer">
      <span className="cl-worktimer__dot" aria-hidden="true" />
      {prefix === '' ? '' : `${prefix} `}
      {formatWorked(now - start)}
    </span>
  );
}

/**
 * An hourly machine job's states, in section 4's words. The goods words
 * ("In Transit", "Delivered") describe a load moving, and a JCB digging a
 * trench is not in transit.
 */
export const MACHINE_STATE: Readonly<
  Record<
    string,
    { readonly label: string; readonly tone: 'pending' | 'info' | 'warning' | 'success' | 'danger' }
  >
> = {
  searching: { label: 'Finding an operator', tone: 'pending' },
  assigned: { label: 'On the way to site', tone: 'info' },
  arrived_pickup: { label: 'At the site', tone: 'warning' },
  in_transit: { label: 'Working', tone: 'success' },
  delivered: { label: 'Work finished', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
};
